import appSettings from "../../settings/appSettings";
import {
  ChildData,
  FilterCriteria,
  FilterCriteriaKey,
  MediaCriteria,
  ParsedMedia,
  RCDSErrorResponse
} from "../../types";
import { wvusLogger } from "..";
import fetchRequest from "../services/fetchRequest";
const { rcdsConfig } = appSettings;

export async function getChildByGuid(
  guid: string,
  options?: {
    maxRetries?: number;
    baseRetryDelay?: number;
    maxRetryDelay?: number;
    timeout?: number;
  }
) {
  const baseUrl = rcdsConfig?.baseURL;
  const endpoint = rcdsConfig?.childDetailByIdEndpoint;
  const requestUrl = `${baseUrl}${endpoint}`.replace("{identifier}", guid);
  const apiKey = rcdsConfig?.apiKey;

  const MAX_RETRIES = options?.maxRetries ?? 2;
  const BASE_RETRY_DELAY = options?.baseRetryDelay ?? 1000;
  const MAX_RETRY_DELAY = options?.maxRetryDelay ?? 3000;
  const TIMEOUT = options?.timeout ?? 10000;

  let retries = 0;
  let lastError;

  while (retries <= MAX_RETRIES) {
    try {
      const headers: HeadersInit = {
        accept: "application/json, text/plain, */*",
        "content-type": "application/json"
      };

      if (apiKey) {
        headers["ocp-apim-subscription-key"] = apiKey;
      }

      const resp = await fetchRequest(requestUrl, {
        method: "GET",
        timeout: TIMEOUT,
        headers
      });

      if (!resp.ok) {
        const errorResult = await resp
          .text()
          .then(parseJson)
          .then((respData: RCDSErrorResponse | string | unknown) => {
            const errorMessage =
              typeof respData === "object" && respData !== null
                ? (respData as RCDSErrorResponse).Message ||
                  (respData as RCDSErrorResponse).message ||
                  String(respData)
                : typeof respData === "string"
                  ? respData
                  : "Error fetching child data";

            return new Error(errorMessage);
          });
        throw errorResult;
      }

      const respData = await resp.json();
      return transformChildData(respData.data);
    } catch (error) {
      lastError = error;

      if (retries === MAX_RETRIES) {
        // All retries used, log and reject
        wvusLogger.log(
          new Error(
            `Error Response from RCDS for get child by GUID after ${MAX_RETRIES + 1} attempts: ${error}`
          ),
          wvusLogger.getLogLevelNames().ERROR
        );
        return Promise.reject(error);
      }

      // Log retry attempt
      wvusLogger.log(
        `Retrying RCDS request for child GUID ${guid} (attempt ${retries + 1}/${MAX_RETRIES}): ${error}`,
        wvusLogger.getLogLevelNames().WARNING
      );

      // Exponential backoff between retries
      const delay = Math.min(BASE_RETRY_DELAY * Math.pow(2, retries), MAX_RETRY_DELAY);
      await new Promise((resolve) => setTimeout(resolve, delay));
      retries++;
    }
  }

  // This should not be reached due to the rejection in the catch block
  return Promise.reject(lastError);
}

export const parseJson = (text: string) => {
  try {
    return JSON.parse(text);
  } catch (error) {
    if (text.length === 264) {
      wvusLogger.log(
        new Error(`Error parsing JSON: ${error}`),
        wvusLogger.getLogLevelNames().ERROR
      );
      return "Invalid response data";
    }
    return text;
  }
};

export function transformChildData(childData: ChildData): ChildData {
  const parseMedia = parseChildMedia(childData);
  return {
    ...childData,
    ...parseMedia
  };
}

export function parseChildMedia(childData: ChildData): ParsedMedia {
  const filterCriteria: FilterCriteria = {
    cgpUrl: {
      contentTypeCode: "CGP",
      format: "web"
    },
    cgpThumbnailUrl: {
      contentTypeCode: "CGP",
      format: "thumb"
    },
    cgvWebUrl: {
      contentTypeCode: "CGV",
      format: "web"
    },
    cgViPadUrl: {
      contentTypeCode: "CGV",
      format: "ipad"
    }
  };

  // Create default media object
  const defaultMedia: ParsedMedia = {
    cgpUrl: null,
    cgpThumbnailUrl: null,
    cgvWebUrl: null,
    cgViPadUrl: null
  };

  // Return default if no child media available
  if (!childData?.childMedia?.length) {
    return defaultMedia;
  }

  // Sort childMedia by mediaData descending (newest first)
  const sortedMedia = [...childData.childMedia].sort((a, b) => {
    const dateA = new Date(a.mediaDate).getTime();
    const dateB = new Date(b.mediaDate).getTime();
    return dateB - dateA;
  });

  return sortedMedia.reduce<ParsedMedia>(
    (data, media) => {
      const criteriaEntries = Object.entries(filterCriteria) as [
        FilterCriteriaKey,
        MediaCriteria
      ][];

      // Skip if we already have all criteria filled
      if (criteriaEntries.every(([key]) => data[key])) {
        return data;
      }

      // Find and assign the first matching criteria
      for (const [key, criteria] of criteriaEntries) {
        if (
          !data[key] &&
          media.contentTypeCode === criteria.contentTypeCode &&
          media.format === criteria.format
        ) {
          data[key] = media.url;
          break;
        }
      }

      return data;
    },
    { ...defaultMedia }
  );
}
